import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes, type ReactNode } from "react";
import { cn } from "./cn.js";

const fieldClasses =
  "w-full rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-3)] " +
  "text-[var(--color-text-primary)] text-[length:var(--font-size-body)] placeholder:text-[var(--color-text-tertiary)] " +
  "transition-colors duration-[var(--motion-fast)] outline-none " +
  "hover:border-[var(--color-text-faint)] focus:border-[var(--color-accent)] focus:bg-[var(--color-surface-2)] " +
  "disabled:opacity-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(fieldClasses, "min-h-[38px] px-3 py-1.5", className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...props },
  ref,
) {
  return <textarea ref={ref} className={cn(fieldClasses, "px-3 py-2 min-h-[76px] resize-y", className)} {...props} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...props },
  ref,
) {
  return (
    <select ref={ref} className={cn(fieldClasses, "min-h-[38px] px-3 pr-8 appearance-none bg-no-repeat", className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='%238a827a' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundPosition: "right 10px center",
      }}
      {...props}
    >
      {children}
    </select>
  );
});

export function Field({ label, children, hint, error, required }: { label: string; children: ReactNode; hint?: string; error?: string; required?: boolean }) {
  return (
    <label className="flex flex-col gap-1 text-[length:var(--font-size-sm)]">
      <span className="font-medium text-[var(--color-text-secondary)]">
        {label}
        {required && <span className="text-[var(--color-danger)] ml-0.5">*</span>}
      </span>
      {children}
      {hint && !error && <span className="text-[var(--color-text-tertiary)] text-[length:var(--font-size-xs)]">{hint}</span>}
      {error && <span className="text-[var(--color-danger)] text-[length:var(--font-size-xs)]">{error}</span>}
    </label>
  );
}
